import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import createCachedSelector from 're-reselect';
import * as api from '../../services/api';
import { selectedBranch } from '../../atoms/shared';
import { SORT_BY_STRING } from '../../utils/sorting';
import useAccount from './useAccount';

const selector = createCachedSelector(
  (data) => data.data,
  (_, branchId) => branchId,
  (_, branchId, locationId) => locationId,
  (data) => {
    const labelSorter = SORT_BY_STRING('label');

    const subLocationsOptions = (data?.subLocations || [])
      .map(({ id, name }) => ({ value: id, label: name }))
      .sort(labelSorter);

    const mappedSubLocations = (data?.subLocations || []).reduce(
      (prev, { id, ...subLocation }) => ({
        ...prev,
        [id]: { ...subLocation, id }
      }),
      {}
    );

    return { location: data, subLocationsOptions, mappedSubLocations };
  }
)({
  keySelector: (_, branchId, locationId, storeKey) => storeKey.join('#')
});

const useSpecificLocation = ({
  branchId: customBranchId,
  locationId,
  enabled = true,
  withAttachments = false
} = {}) => {
  const { accountId } = useAccount();

  const defaultBranchId = useRecoilValue(selectedBranch);
  const branchId = customBranchId ? parseInt(customBranchId, 10) : defaultBranchId;

  const storeKey = useMemo(() => ['sub_locations', accountId, branchId, locationId], [accountId, branchId, locationId]);

  const { isLoading, data, refetch } = useQuery(
    storeKey,
    () => api.fetchBranchLocation({ accountId, branchId, locationId, withAttachments }),
    {
      enabled: !!enabled && !!locationId && !!branchId,
      select: (locationData) => selector(locationData, branchId, locationId, storeKey)
    }
  );

  const { mutateAsync: addSubLocation } = useMutation(({ locationId: subLocationId, clientBranchId }) => {
    return api.addSubLocation({ accountId, branchId, subLocationId, locationId, clientBranchId });
  });

  const { location, subLocationsOptions, mappedSubLocations } = data || {};

  return {
    isLoading,
    data: location,
    subLocations: location?.subLocations,
    refetch,
    subLocationsOptions,
    mappedSubLocations,
    addSubLocation
  };
};

export default useSpecificLocation;
